import { isEmpty, throttle } from 'lodash';
import React, { Component, forwardRef } from 'react';
import { FormattedMessage, injectIntl } from 'react-intl';
import { connect } from 'react-redux';

import {
  intakeFormSet,
  intakeFormUpdateImplicitField,
  intakeFormValidate,
} from '~/actions/intakeForm';
import { ticketUpdate } from '~/actions/ticket';
import { createTicket } from '~/api/tickets';
import { getWorkflowIntakeForm } from '~/api/workflows';
import { MAX_DOCUMENT_NAME_LENGTH } from '~/common/constants/max_lengths';
import { showToast } from '~/components/Shared/EcToast/showToast';
import FallbackContent from '~/components/Shared/FallbackContent/FallbackContent';
import WorkflowForm from '~/components/Workflow/Form/WorkflowForm';
import WorkflowFormSectionHeader from '~/components/Workflow/Form/WorkflowFormSectionHeader';
import { Button } from '~/eds/src/components/Button/Button';
import { ContentContainer } from '~/eds/src/components/ContentContainer/ContentContainer';
import { DescriptionText } from '~/eds/src/components/DescriptionText/DescriptionText';
import { Section } from '~/eds/src/components/Section/Section';
import { FileExtensionType } from '~/enums/FileExtensionType';
import { HttpStatusCodeType } from '~/enums/HttpStatusCodeType';
import { WorkflowIntakeFormType } from '~/enums/WorkflowIntakeFormType';
import { PartySelectField } from '~/features/turn-tracking/PartySelectField';
import { FlagType } from '~/flags/flags';
import { withFlags } from '~/flags/withFlags';
import { getAmendIntakeForm } from '~/redux/api/methods/workflows/getAmendIntakeForm';
import { getIntegrateWorkflowIntakeForm } from '~/redux/api/methods/workflows/getIntegrateWorkflowIntakeForm';
import { withRouting } from '~/routing/withRouting';
import { ERROR, SUCCESS, WARNING } from '~/types/toast.types';
import Alert from '~/ui/components/Alert/Alert';
import Box from '~/ui/components/Box/Box';
import FileInput from '~/ui/components/FileInput/FileInput';
import FlexLayout from '~/ui/components/FlexLayout/FlexLayout';
import Icon from '~/ui/components/Icon/Icon';
import Link from '~/ui/components/Link/Link';
import RadioGroup from '~/ui/components/RadioGroup/RadioGroup';
import Text from '~/ui/components/Text/Text';
import { downloadFile, getFileIcon } from '~/utils/files';
import { copyToClipboard } from '~/utils/helper.utils';
import { isFormValid, testRequiredQuestionsAnswered } from '~/utils/intakeForm';
import {
  getAcceptedFiles,
  prepareIntakeFormSubmitData,
} from '~/utils/workflow';

import { TicketFormSidebar } from './TicketFormSidebar';
import { validateSupplierAndCompany } from './validateSupplierAndCompany';
import WizardActionPage from './WizardActionPage';

const MARGIN_OFFSET = 64;

function getShouldRenderCounterpartyUpload(workflowType) {
  return [
    WorkflowIntakeFormType.Counterparty,
    WorkflowIntakeFormType.CompanyAndCounterparty,
  ].includes(workflowType);
}

const SCROLL_PADDING = '100vh';

class TicketForm extends Component {
  constructor(props) {
    super(props);

    this.state = {
      error: null,
      activeSectionId: null,
      counterpartyFile: null,
      forCounterpartyUpload: null,
      isLoadingForm: true,
      isSubmittingForm: false,
      isCounterpartyPaperSectionCollapsed: false,
      isShowingWizardActionPage: false,
      isTurnTrackingCollapsed: false,
      loadingMessageOnSubmit: null,
      ticketId: null,
      turnTrackingSelectedPartyId: null,
    };
  }

  goBack = () => {
    this.props.navigate('/workflow/tickets');
  };

  setIntakeFormType() {
    const { intakeFormUpdateImplicitField } = this.props;
    const { forCounterpartyUpload, type } = this.state;
    if (type === WorkflowIntakeFormType.CompanyAndCounterparty) {
      intakeFormUpdateImplicitField({
        id: 'Paper Type',
        value: {
          value: forCounterpartyUpload
            ? WorkflowIntakeFormType.Counterparty
            : WorkflowIntakeFormType.Company,
        },
      });
    }
  }

  setUpSectionAndUpload() {
    this.setIntakeFormType();
  }

  componentDidMount() {
    const {
      params: { workflowId },
      isAmendWorkflow,
    } = this.props;

    if (isAmendWorkflow) {
      this.getIntakeForm(null);
    } else {
      this.getIntakeForm(workflowId);
    }
  }

  componentDidUpdate(prevProps, prevState) {
    const { intl } = this.props;
    const { loadingMessageOnSubmit: prevMessage } = prevState;
    const { intakeForm, onUpdateFormState } = this.props;
    const { counterpartyFile, forCounterpartyUpload, isSubmittingForm, type } =
      this.state;

    const shouldRenderCounterpartyUpload =
      getShouldRenderCounterpartyUpload(type);

    const LOADING_MESSAGES = {
      1: shouldRenderCounterpartyUpload
        ? intl.formatMessage({
            id: 'TicketForm.uploadingDocumentMessage',
            defaultMessage: 'Uploading your document',
            description: 'Loading message when uploading document',
          })
        : intl.formatMessage({
            id: 'TicketForm.generatingDocumentMessage',
            defaultMessage: 'Generating document from template',
            description:
              'Loading message when generating document from template',
          }),
      2: intl.formatMessage({
        id: 'TicketForm.preparingDocumentMessage',
        defaultMessage: 'Preparing your document',
        description: 'Loading message when preparing document',
      }),
    };

    // if intakeForm is empty we don't evaluate questions because it errors out
    const hasAllRequiredQuestionsAnswered = isEmpty(intakeForm)
      ? false
      : testRequiredQuestionsAnswered(intakeForm);
    let isDisabled;
    let disabledTooltipContent;

    if (isSubmittingForm) {
      if (!prevMessage) {
        this.setState({
          ...this.state,
          loadingMessageOnSubmit: LOADING_MESSAGES[1],
        });
      }
      if (prevMessage === LOADING_MESSAGES[1]) {
        const updateLoadingMessage = () => {
          this.setState({
            ...this.state,
            loadingMessageOnSubmit: LOADING_MESSAGES[2],
          });
        };
        setTimeout(updateLoadingMessage, 3000);
      }
    }

    if (
      type === WorkflowIntakeFormType.Counterparty ||
      (type === WorkflowIntakeFormType.CompanyAndCounterparty &&
        forCounterpartyUpload)
    ) {
      isDisabled = !counterpartyFile || !hasAllRequiredQuestionsAnswered;
      if (isDisabled) {
        disabledTooltipContent = intl.formatMessage({
          id: 'TicketForm.fillFieldsAndUploadTooltip',
          defaultMessage:
            'Please fill out all required fields in the form and upload a counterparty paper',
          description:
            'Tooltip when form fields and counterparty upload are required',
        });
      }
    } else {
      isDisabled = !hasAllRequiredQuestionsAnswered;
      if (isDisabled) {
        disabledTooltipContent = intl.formatMessage({
          id: 'TicketForm.fillRequiredFieldsTooltip',
          defaultMessage: 'Please fill out all required fields in the form',
          description: 'Tooltip when required form fields need to be filled',
        });
      }
    }
    onUpdateFormState({
      isSubmitDisabled: isDisabled,
      disabledSubmitTooltip: disabledTooltipContent,
    });
  }

  getIntakeForm = (workflowId, formData, native) => {
    const {
      intakeFormSet,
      isAmendWorkflow,
      isIntegrateWorkflow,
      referenceId,
      params,
      flags,
    } = this.props;

    let fetchIntakeForm;
    if (isAmendWorkflow) {
      fetchIntakeForm = getAmendIntakeForm({
        contractRecordUniqueId: params.contractRecordUniqueId,
      });
    } else if (isIntegrateWorkflow) {
      fetchIntakeForm = getIntegrateWorkflowIntakeForm({
        id: workflowId,
        referenceId,
      });
    } else {
      fetchIntakeForm = getWorkflowIntakeForm(workflowId);
    }

    return fetchIntakeForm
      .then((res) => {
        const isWSSIntegrationPhase2M1M3Enabled =
          flags[FlagType.WSSIntegrationPhase2M1M3] ?? false;
        intakeFormSet({
          res,
          formData,
          native,
          isWSSIntegrationPhase2M1M3Enabled,
        });

        const hasWellKnownFields =
          flags[FlagType.WSSIntegrationPhase2M1M3] &&
          res.wellKnownFields &&
          Object.keys(res.wellKnownFields).length > 0;
        const initialSectionId = hasWellKnownFields
          ? 'well-known-fields'
          : res.sections[0]?.id;

        this.setState(
          {
            isLoadingForm: false,
            activeSectionId: initialSectionId,
            type: res.type,
            forCounterpartyUpload:
              res.type === WorkflowIntakeFormType.Counterparty,
          },
          this.setUpSectionAndUpload,
        );
      })
      .catch((e) => {
        this.setState({ isLoadingForm: false, error: e.response?.status });
        throw e;
      });
  };

  renderErrorMessage(error) {
    const { intl } = this.props;
    let icon = 'reject';
    let title = intl.formatMessage({
      id: 'TicketForm.genericErrorTitle',
      defaultMessage: 'An error occurred while fetching an intake form.',
      description: 'Generic error title when fetching intake form fails',
    });
    let content = intl.formatMessage({
      id: 'TicketForm.contactSupportContent',
      defaultMessage: 'Contact our support team.',
      description: 'Message to contact support team',
    });

    if (error === HttpStatusCodeType.Forbidden) {
      icon = 'lock';
      title = intl.formatMessage({
        id: 'TicketForm.permissionErrorTitle',
        defaultMessage: "Sorry, you don't have permission to access this page.",
        description: 'Error title when user lacks permission',
      });
      content = intl.formatMessage({
        id: 'TicketForm.contactAdminContent',
        defaultMessage: 'Contact your administrator to request access.',
        description: 'Message to contact administrator for access',
      });
    } else if (error === HttpStatusCodeType.NotFound) {
      icon = 'documentOutline';
      title = intl.formatMessage({
        id: 'TicketForm.notFoundErrorTitle',
        defaultMessage: 'The page you requested does not exist.',
        description: 'Error title when page is not found',
      });
      content = intl.formatMessage({
        id: 'TicketForm.notFoundErrorContent',
        defaultMessage:
          'If you think this is a mistake, please contact our support team.',
        description: 'Error content when page is not found',
      });
    }

    return (
      <FallbackContent
        icon={<Icon color="red-600" icon={icon} size="xl" />}
        title={title}
        content={content}
        button={
          <Button
            text={intl.formatMessage({
              id: 'TicketForm.goBackButton',
              defaultMessage: 'Go back to Tickets',
              description: 'Button text to go back to tickets page',
            })}
            variant="secondary"
            onClick={this.goBack}
          />
        }
      />
    );
  }

  onFormSubmit = () => {
    const { intl, isAmendWorkflow } = this.props;
    const { counterpartyFile, turnTrackingSelectedPartyId } = this.state;
    const { intakeForm, intakeFormValidate } = this.props;
    const { name, workflowId, fields } = intakeForm;
    const workflowType = intakeForm?.workflow?.type;

    intakeFormValidate();

    const needsCounterpartyFile =
      workflowType === WorkflowIntakeFormType.Counterparty ||
      (workflowType === WorkflowIntakeFormType.CompanyAndCounterparty &&
        this.state.forCounterpartyUpload);

    if (needsCounterpartyFile && !counterpartyFile) {
      showToast(
        WARNING,
        intl.formatMessage({
          id: 'TicketForm.uploadCounterpartyWarning',
          defaultMessage: 'Please upload a counterparty document.',
          description:
            'Warning message when counterparty document is required but not uploaded',
        }),
      );
      return;
    }

    if (
      counterpartyFile &&
      counterpartyFile.name.length > MAX_DOCUMENT_NAME_LENGTH
    ) {
      showToast(
        ERROR,
        intl.formatMessage(
          {
            id: 'TicketForm.fileNameTooLongError',
            defaultMessage:
              'Uploaded file name cannot be longer than {maxLength} characters.',
            description:
              'Error message when uploaded file name exceeds maximum length',
          },
          { maxLength: MAX_DOCUMENT_NAME_LENGTH },
        ),
      );
    } else if (!isFormValid(intakeForm)) {
      showToast(
        WARNING,
        intl.formatMessage({
          id: 'TicketForm.fillFieldsWarning',
          defaultMessage: 'Please fill in all fields correctly.',
          description:
            'Warning message when form fields are not filled correctly',
        }),
      );
    } else {
      const submitTicket = () => {
        this.setState({ isSubmittingForm: true });

        const intakeFormData = prepareIntakeFormSubmitData({
          name,
          workflowId,
          fields,
        });

        createTicket({
          intakeForm: intakeFormData,
          counterpartyFile,
          turnTracking: {
            partyId: turnTrackingSelectedPartyId,
          },
          referenceId: this.props.referenceId,
          contractRecordUniqueId: this.props.params.contractRecordUniqueId,
        })
          .then((ticket) => {
            showToast(
              SUCCESS,
              intl.formatMessage({
                id: 'TicketForm.ticketCreatedSuccess',
                defaultMessage: 'A new ticket has been successfully created.',
                description: 'Success message when ticket is created',
              }),
            );
            this.props.ticketUpdate({ stages: ticket.stages });
            this.setState({
              isShowingWizardActionPage: true,
              ticketId: ticket.id,
            });
          })
          .catch((err) => {
            const responseData = err?.response?.data;
            if (
              responseData?.code === HttpStatusCodeType.UnprocessableContent
            ) {
              showToast(
                ERROR,
                responseData?.message,
                responseData?.details,
                10000,
                true,
              );
            } else {
              showToast(
                ERROR,
                intl.formatMessage({
                  id: 'TicketForm.genericCreateError',
                  defaultMessage:
                    'Sorry, something has gone wrong. Please check our Workflow Help for more information or to Submit a request.',
                  description:
                    'Generic error message when ticket creation fails',
                }),
                'https://doc.workday.com/evisort/en-us/contract-management-and-document-intelligence/workflow/workflow-builder-overview.html',
                10000,
                true,
              );
            }
          })
          .finally(() => {
            this.setState({ isSubmittingForm: false });
          });
      };

      if (isAmendWorkflow) {
        validateSupplierAndCompany({
          fields,
          workflowId,
          intl,
        }).then((validationErrors) => {
          if (validationErrors.length > 0) {
            validationErrors.forEach((error) => showToast(ERROR, error));
            return;
          }
          submitTicket();
        });
      } else {
        submitTicket();
      }
    }
  };

  onSidebarSectionClick = (activeSectionId) => {
    const activeSectionElement = document.getElementById(activeSectionId);
    activeSectionElement.scrollIntoView();
    this.setState({ activeSectionId });
  };

  // TODO: spec and build a formal ClipboardBox component allowing copying contents to a clipboard with relevant tooltip display.
  handleOnCopyAction = () => {
    this.setState({ copiedTooltip: true });
    copyToClipboard(window.location.href, null, null, this.handleCopiedTooltip);
  };

  handleCopiedTooltip = () => {
    setTimeout(() => {
      this.setState({ copiedTooltip: false });
    }, 2000);
  };

  renderUploadOptionText(type) {
    const { intl } = this.props;
    const { counterpartyFile, forCounterpartyUpload } = this.state;

    const updatedCounterpartyDocumentOptions = [
      {
        label: intl.formatMessage({
          id: 'TicketForm.generateNewContractOption',
          defaultMessage:
            'I would like to generate a new contract from our template',
          description: 'Option to generate new contract from template',
        }),
        value: false,
      },
      {
        label: intl.formatMessage({
          id: 'TicketForm.submitCounterpartyContractOption',
          defaultMessage:
            'I am submitting a contract I received from the counterparty',
          description: 'Option to submit received counterparty contract',
        }),
        value: true,
      },
    ];

    if (type === WorkflowIntakeFormType.Counterparty) {
      return intl.formatMessage({
        id: 'TicketForm.existingDocumentRequired',
        defaultMessage: 'This workflow requires an existing document.',
        description: 'Message indicating existing document is required',
      });
    }
    return (
      <>
        {intl.formatMessage({
          id: 'TicketForm.contractSubmissionQuestion',
          defaultMessage:
            'Are you submitting a contract you received from a counterparty or will you be generating our template contract?',
          description: 'Question about contract submission type',
        })}
        <Box mt={5}>
          <RadioGroup
            onChange={(value) => {
              if (!value && counterpartyFile) {
                this.setState({ counterpartyFile: null });
              }
              this.setState(
                { forCounterpartyUpload: value },
                this.setIntakeFormType,
              );
            }}
            options={updatedCounterpartyDocumentOptions}
            value={forCounterpartyUpload}
          />
        </Box>
      </>
    );
  }

  renderCounterpartyPaperUpload() {
    const { intl } = this.props;
    const {
      counterpartyFile,
      forCounterpartyUpload,
      isCounterpartyPaperSectionCollapsed,
      type,
    } = this.state;
    const shouldRenderCounterpartyUpload =
      getShouldRenderCounterpartyUpload(type);
    const { workflow } = this.props.intakeForm;

    if (shouldRenderCounterpartyUpload) {
      return (
        <FlexLayout
          flexDirection="column"
          mb={8}
          sx={{ border: 'border', borderRadius: 'm' }}
        >
          <WorkflowFormSectionHeader
            isCollapsed={isCounterpartyPaperSectionCollapsed}
            title={intl.formatMessage({
              id: 'TicketForm.documentSectionTitle',
              defaultMessage: 'Document',
              description: 'Title for document section',
            })}
            onToggleCollapse={() =>
              this.setState({
                isCounterpartyPaperSectionCollapsed:
                  !this.state.isCounterpartyPaperSectionCollapsed,
              })
            }
          />
          {!isCounterpartyPaperSectionCollapsed && (
            <FlexLayout flexDirection="column" p={4}>
              <FlexLayout space={3} alignItems="center">
                <Text variant="xs-dense" color="gray-600">
                  {this.renderUploadOptionText(type)}
                </Text>
              </FlexLayout>
              {counterpartyFile ? (
                <FlexLayout
                  alignItems="center"
                  bg="gray-200"
                  justifyContent="space-between"
                  mt={5}
                  mb={5}
                  px={3}
                  space={6}
                  sx={{
                    borderRadius: 'm',
                    maxWidth: '720px',
                    width: '100%',
                    height: '50px',
                  }}
                >
                  <FlexLayout alignItems="center" space={4}>
                    <Icon icon={getFileIcon(counterpartyFile)} />
                    <Text variant="xs-dense-bold">{counterpartyFile.name}</Text>
                  </FlexLayout>
                  <FlexLayout space={6}>
                    <Icon
                      color="gray-600"
                      icon="saveTo"
                      size="s"
                      onClick={() => downloadFile(counterpartyFile)}
                    />
                    <Icon
                      color="gray-600"
                      icon="trash"
                      size="s"
                      onClick={() => this.setState({ counterpartyFile: null })}
                    />
                  </FlexLayout>
                </FlexLayout>
              ) : forCounterpartyUpload ? (
                <FlexLayout mt={5}>
                  <FileInput
                    acceptedFiles={getAcceptedFiles(workflow, [
                      FileExtensionType.Docx,
                    ])}
                    enableDropzone
                    enableUpload
                    onChange={(counterpartyFile) => {
                      this.setState({ counterpartyFile: counterpartyFile[0] });
                    }}
                  />
                </FlexLayout>
              ) : null}
            </FlexLayout>
          )}
        </FlexLayout>
      );
    }
  }

  renderTurnTracking() {
    const { intl } = this.props;
    const { isTurnTrackingCollapsed } = this.state;
    const { intakeForm } = this.props;

    return (
      <FlexLayout
        flexDirection="column"
        mb={8}
        sx={{ border: 'border', borderRadius: 'm' }}
      >
        <WorkflowFormSectionHeader
          isCollapsed={isTurnTrackingCollapsed}
          title={intl.formatMessage({
            id: 'TicketForm.cycleTimeTrackingTitle',
            defaultMessage: 'Cycle Time Tracking',
            description: 'Title for cycle time tracking section',
          })}
          onToggleCollapse={() =>
            this.setState({
              isTurnTrackingCollapsed: !this.state.isTurnTrackingCollapsed,
            })
          }
        />
        {!isTurnTrackingCollapsed && (
          <FlexLayout mt={5} mb={5} px={3}>
            <PartySelectField
              workflowId={intakeForm.workflowId}
              onChange={(partyId) =>
                this.setState({ turnTrackingSelectedPartyId: partyId })
              }
              showDescription={true}
              errorMessage={{
                message: intl.formatMessage({
                  id: 'TicketForm.startNewTicketMessage',
                  defaultMessage: 'Start a new ticket to retry.',
                  description: 'Message to start new ticket when error occurs',
                }),
                title: intl.formatMessage({
                  id: 'TicketForm.loadingPartiesErrorTitle',
                  defaultMessage:
                    "We're experiencing difficulties loading Responsible Parties",
                  description:
                    'Error title when loading responsible parties fails',
                }),
              }}
              width="input.l.width"
            />
          </FlexLayout>
        )}
      </FlexLayout>
    );
  }

  sectionsContainer = React.createRef();

  _handleWorkflowFormScroll = () => {
    if (!this.sectionsContainer.current) return;

    const visibleElements = [];
    const sections = Array.from(
      this.sectionsContainer.current.lastElementChild?.children || [],
    );
    sections.forEach((el) => {
      const rectangle = el.getBoundingClientRect();
      const viewHeight = Math.max(
        document.documentElement.clientHeight,
        window.innerHeight,
      );
      const isVisible = !(
        rectangle.bottom <= MARGIN_OFFSET ||
        rectangle.top - viewHeight >= MARGIN_OFFSET
      );
      if (isVisible) visibleElements.push(el.id);
    });

    if (this.state.activeSectionId !== visibleElements[0]) {
      const activeSectionId = visibleElements[0];

      this.setState({ activeSectionId });
    }
  };

  handleWorkflowFormScroll = throttle(this._handleWorkflowFormScroll, 50);

  render() {
    const { intakeForm, intl } = this.props;
    const {
      error,
      activeSectionId,
      isLoadingForm,
      isShowingWizardActionPage,
      isSubmittingForm,
      loadingMessageOnSubmit,
      ticketId,
    } = this.state;

    const enableTurnTracking = intakeForm.isTurnTrackingEnabled;

    if (isLoadingForm) {
      return (
        <ContentContainer
          loadingContent={{
            isLoading: true,
            description: intl.formatMessage({
              id: 'TicketForm.loadingFormMessage',
              defaultMessage: 'Loading form…',
              description: 'Message shown while loading form',
            }),
          }}
        />
      );
    }

    if (isSubmittingForm) {
      return (
        <ContentContainer
          loadingContent={{
            isLoading: true,
            description: loadingMessageOnSubmit,
          }}
        />
      );
    }

    if (error) {
      return <>{this.renderErrorMessage(error)}</>;
    }

    if (!intakeForm.fields) {
      return null;
    }

    if (isShowingWizardActionPage) {
      return <WizardActionPage ticketId={ticketId} intakeForm={intakeForm} />;
    }

    return (
      <div onScroll={this.handleWorkflowFormScroll}>
        <FlexLayout space={6}>
          <TicketFormSidebar
            activeSectionId={activeSectionId}
            onSidebarSectionClick={this.onSidebarSectionClick}
            isSubmittingForm={isSubmittingForm}
            intakeForm={intakeForm}
          />
          <FlexLayout
            ref={this.sectionsContainer}
            flexDirection="column"
            pb={SCROLL_PADDING}
            space={4}
            sx={{ width: '100%' }}
          >
            {intakeForm.hasErrors ? (
              <Alert enableIcon variant="danger">
                <FormattedMessage
                  id="TicketForm.workflowErrorMessage"
                  defaultMessage="This workflow has errors, please <link>edit</link> it to be able to create a ticket from it."
                  description="Message shown when workflow has errors"
                  values={{
                    link: (chunks) => (
                      <Link
                        variant="s-dense-bold"
                        to={`/workflow/builder/${intakeForm.workflowId}`}
                      >
                        {chunks}
                      </Link>
                    ),
                  }}
                />
              </Alert>
            ) : (
              <>
                {intakeForm.description && (
                  <Section
                    title={intl.formatMessage({
                      id: 'TicketForm.descriptionSectionTitle',
                      defaultMessage: 'Description',
                      description: 'Title for description section',
                    })}
                  >
                    <DescriptionText text={intakeForm.description} />
                  </Section>
                )}
                {this.renderCounterpartyPaperUpload()}
                {enableTurnTracking && this.renderTurnTracking()}
                <WorkflowForm />
              </>
            )}
          </FlexLayout>
        </FlexLayout>
      </div>
    );
  }
}

const withRouterAndRefAndFlags = (WrappedComponent) => {
  const WithFlagsComponent = withFlags(({ innerRef, ...props }) => (
    <WrappedComponent ref={innerRef} {...props} />
  ));
  const WithRouterComponent = withRouting(({ forwardRef, ...props }) => (
    <WithFlagsComponent innerRef={forwardRef} {...props} />
  ));
  const WithIntlComponent = injectIntl(({ forwardRef, ...props }) => (
    <WithRouterComponent innerRef={forwardRef} {...props} />
  ));
  const WithRouterAndRefComponent = forwardRef((props, ref) => (
    <WithIntlComponent forwardRef={ref} {...props} />
  ));
  return WithRouterAndRefComponent;
};

const mapStateToProps = ({ intakeForm }) => ({ intakeForm });

TicketForm = withRouterAndRefAndFlags(
  connect(
    mapStateToProps,
    {
      intakeFormSet,
      intakeFormValidate,
      intakeFormUpdateImplicitField,
      ticketUpdate,
    },
    null,
    {
      forwardRef: true,
    },
  )(TicketForm),
);

export default TicketForm;
